import React, { createContext, useContext, useMemo } from 'react'
import { useMembersContext } from './MembersProvider'

const PendingInvitesContext = createContext()

/**
 * @typedef {{
 * singleUseInvites: import('@fs/zion-tree-groups').Invite[]
 * multiUseInvites: import('@fs/zion-tree-groups').Invite[]
 * }} Invites
 *
 * @returns {{
 * invitesToShow: Invites
 * refetchInvites: () => void
 * }}
 */

export const usePendingInvites = () => {
  const context = useContext(PendingInvitesContext)
  if (!context) throw new Error('You need to use the PendingInvitesProvider to use usePendingInvites')
  return context
}

/**
 * Requires MembersProvider and GroupManagementGroupProvider above it
 * @param {{
 * singleUseInvites: import('@fs/zion-tree-groups').SingleUseInvite[]
 * multiUseInvites: import('@fs/zion-tree-groups').MultiUseInvite[]
 * refetchInvites: () => void
 * children: import('react').ReactNode
 * group: import('@fs/zion-tree-types').GroupDetails | import('@fs/zion-tree-types').GroupSummary
 * }} props
 * @returns
 */

export default function PendingInvitesProvider({ singleUseInvites, multiUseInvites, refetchInvites, children, group }) {
  const isResearcherGroup = group.types.includes('RESEARCHER_TREE')
  const { currentMemberDetails, userIsAdmin } = useMembersContext()

  const ownInvites = useMemo(
    () => ({
      multiUseInvites: multiUseInvites.filter((invite) => invite.inviterCisId === currentMemberDetails?.memberCisId),
      singleUseInvites: singleUseInvites.filter((invite) => invite.inviterCisId === currentMemberDetails?.memberCisId),
    }),
    [multiUseInvites, singleUseInvites, currentMemberDetails?.memberCisId]
  )

  const invitesToShow = useMemo(
    () => ({
      /* eslint-disable no-nested-ternary -- it is simpler this way */
      multiUseInvites: userIsAdmin ? multiUseInvites : !isResearcherGroup ? ownInvites.multiUseInvites : [],
      singleUseInvites: userIsAdmin ? singleUseInvites : !isResearcherGroup ? ownInvites.singleUseInvites : [],
      /* eslint-enable no-nested-ternary -- re-enable */
    }),
    [
      multiUseInvites,
      singleUseInvites,
      userIsAdmin,
      ownInvites.multiUseInvites,
      ownInvites.singleUseInvites,
      isResearcherGroup,
    ]
  )

  const value = useMemo(() => ({ invitesToShow, refetchInvites }), [invitesToShow, refetchInvites])
  return <PendingInvitesContext.Provider value={value}>{children}</PendingInvitesContext.Provider>
}
